import { CloseOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Divider } from 'antd';
import { useState } from 'react';
import colorTokens from '../../../constants/design_system/colors';
import { useGetWorkspace } from '../../../serverState/workspace';
import { WorkspaceEstimationType } from '../../../types/Workspace';
import { TaskEstimateLongMapping } from '../../TaskTable/helpers/helper';
import { TaskEstimateComponentType, TaskEstimateLabel } from '../TaskEstimateLabel';
import BaseDropdown from './BaseDropdown';
import NoDragWrapper from './NoDragWrapper';

export type EstimateDropdownProps = {
  estimate: number | null;
  size: 'small' | 'medium' | 'large';
  onSelect?: (estimate: number | null) => void;
  onBlur?: () => void;
  onEditEstimationSettings?: () => void;
  hideOutline?: boolean;
  defaultOpen?: boolean;
  selectedElement?: React.ReactNode;
  emptyElement?: React.ReactNode;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const EstimateDropdown = (props: EstimateDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const workspaceQuery = useGetWorkspace();
  const estimationType = workspaceQuery.data?.estimationType ?? WorkspaceEstimationType.storyPoints;

  const onSelect = (value: number | null) => {
    if (props.onSelect) {
      props.onSelect(value);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  return (
    <NoDragWrapper className="h-full">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isDropdownOpen}
        placement={props.position || 'bottomLeft'}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (!open && props.onBlur) {
            props.onBlur();
          }
        }}
        dropdownRender={(menu) => (
          <div className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg p-2 gap-2">
            {Object.keys(TaskEstimateLongMapping(estimationType)).map((keyStr) => (
              <div
                className="flex items-center justify-start cursor-pointer hover:bg-neutral-200"
                key={keyStr}
                onClick={() => onSelect(Number(keyStr))}
              >
                <TaskEstimateLabel
                  estimate={Number(keyStr)}
                  component={TaskEstimateComponentType.dropdown}
                  estimationType={estimationType}
                  className="h-5"
                />
              </div>
            ))}
            {(props.estimate || props.onEditEstimationSettings) && <Divider className="m-0 mt-1" />}
            {props.estimate && (
              <div>
                <Button
                  className="flex justify-start items-center text-sm font-normal text-neutral-900 border-none pl-1 shadow-none w-full text-left"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(null);
                  }}
                  icon={<CloseOutlined />}
                >
                  Remove estimate
                </Button>
              </div>
            )}
            {props.onEditEstimationSettings && (
              <Button
                className="flex justify-start items-center text-sm font-normal text-neutral-900 border-none pl-1 shadow-none w-full text-left"
                onMouseDown={(e) => {
                  e.preventDefault();
                  props.onEditEstimationSettings?.();
                  setDropdownOpen(false);
                }}
                icon={<SettingOutlined />}
              >
                Estimation Settings
              </Button>
            )}
          </div>
        )}
      >
        <div
          className={`${props.className ?? 'h-full'} flex items-center cursor-pointer`}
          style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        >
          {props.selectedElement ||
            (props.estimate ? (
              <TaskEstimateLabel
                estimate={props.estimate}
                component={
                  props.size === 'small' ? TaskEstimateComponentType.table : TaskEstimateComponentType.dropdown
                }
                estimationType={estimationType}
                className="h-6"
              />
            ) : (
              props.emptyElement || (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="px-1 text-neutral-900 text-sm font-normal">Estimate</div>
                </div>
              )
            ))}
        </div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

export default EstimateDropdown;
